import { useState, useCallback, useEffect, useMemo } from 'react'
import req from '@sportninja/common/api/request'
import type { ConstraintTier } from '../constants'
import {
  TEAM_CONSTRAINT_CODES,
  DIVISION_CONSTRAINT_CODES,
  ALL_CONSTRAINT_CODES,
  GLOBAL_ONLY_CONSTRAINT_CODES,
} from '../constants'

/**
 * Constraint type definition from API
 */
export interface ConstraintTypeDefinition {
  id: string
  code: string
  name: string
  description: string | null
  category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
  is_hard_default: boolean
  default_priority: number
  default_weight: number
  parameter_schema: Record<string, unknown> | null
}

/**
 * Hook result interface
 */
interface UseConstraintTypesResult {
  /** List of available constraint types */
  constraintTypes: ConstraintTypeDefinition[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Refetch constraint types */
  refetch: () => Promise<void>
  /** Get constraint type by code */
  getTypeByCode: (code: string) => ConstraintTypeDefinition | undefined
  /** Get constraint types by category */
  getTypesByCategory: (
    category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
  ) => ConstraintTypeDefinition[]
}

/**
 * Constraint type codes currently supported in the UI.
 * Derived from the tier arrays in constants.ts — single source of truth.
 */
const ALLOWED_CONSTRAINT_CODES = ALL_CONSTRAINT_CODES

/**
 * Custom hook for fetching and managing scheduler constraint types
 *
 * @returns Object containing constraint types data, loading states, and helper functions
 */
const TIER_CODE_MAP: Record<string, readonly string[]> = {
  team: TEAM_CONSTRAINT_CODES,
  division: DIVISION_CONSTRAINT_CODES,
  global: GLOBAL_ONLY_CONSTRAINT_CODES,
}

export const useConstraintTypes = (
  tier?: ConstraintTier
): UseConstraintTypesResult => {
  const [constraintTypes, setConstraintTypes] = useState<
    ConstraintTypeDefinition[]
  >([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetch all constraint types
   */
  const fetchConstraintTypes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data } = await req('/scheduler/constraint-types')
      // Only include constraint types that are in the allowed list
      const filteredData = (data || []).filter(
        (type: ConstraintTypeDefinition) =>
          ALLOWED_CONSTRAINT_CODES.includes(type.code)
      )
      setConstraintTypes(filteredData)
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching constraint types')
      )
      setConstraintTypes([])
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Get constraint type by code
   */
  const getTypeByCode = useCallback(
    (code: string): ConstraintTypeDefinition | undefined => {
      return constraintTypes.find((t) => t.code === code)
    },
    [constraintTypes]
  )

  /**
   * Get constraint types by category
   */
  const getTypesByCategory = useCallback(
    (
      category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
    ): ConstraintTypeDefinition[] => {
      return constraintTypes.filter((t) => t.category === category)
    },
    [constraintTypes]
  )

  // Apply tier filter on top of the fetched types
  const filteredByTier = useMemo(() => {
    if (!tier) {
      return constraintTypes
    }
    const allowed = TIER_CODE_MAP[tier]
    if (!allowed) {
      return constraintTypes
    }
    return constraintTypes.filter((t) => allowed.includes(t.code))
  }, [constraintTypes, tier])

  // Fetch constraint types on mount
  useEffect(() => {
    fetchConstraintTypes()
  }, [fetchConstraintTypes])

  return {
    constraintTypes: filteredByTier,
    loading,
    error,
    refetch: fetchConstraintTypes,
    getTypeByCode,
    getTypesByCategory,
  }
}

export default useConstraintTypes
